import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Download, Plus, Save, Send, X, Search, Filter, FilterX, Upload, List, LayoutGrid, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Product } from "@/features/inventory/types";
import { PurchaseOrder, PurchaseOrderItem } from "../types";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getStockStatus } from "@/features/inventory/utils/stockUtils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import ProductImageWithHover from "./ProductImageWithHover";
import { ConvertedCostIndicator } from "@/components/cost/ConvertedCostIndicator";
import { reportError } from "@/lib/logging";
import { toCsvContent } from "@/lib/csv";
import { toast } from "@/hooks/use-toast";
import { purchaseOrderFormSchema, type PurchaseOrderFormData, validateOrderQuantities } from "../types/purchase-order-form.types";
import PurchaseOrderCsvUploadModal from "./PurchaseOrderCsvUploadModal";
import { useSuppliers } from "@/features/suppliers/hooks/useSuppliers";
import QuickAddSupplierModal from "@/features/suppliers/components/QuickAddSupplierModal";
import { useCurrencyFormatter } from "@/hooks/useCurrencyFormatter";
import { useTranslation } from "react-i18next";
import { STATUS_LABELS, STATUS_SELECT_CLASS } from "@/features/purchase-orders/utils/status-style";
import { SUPPLIER_MATCH_TIER, getSupplierMatchTier } from "@/features/purchase-orders/utils/supplier-match";
import { cn } from "@/lib/utils";
import { useRole } from "@/hooks/use-role";

interface SubmitOrderTabProps {
  products: Product[];
  loading: boolean;
  onCreateOrder: (supplierName: string, supplierId: string | null, orderQuantities: Record<string, number>, title?: string, description?: string, etaDate?: string) => Promise<void>;
  onCreateDraftOrder: (supplierName: string, supplierId: string | null, orderQuantities: Record<string, number>, title?: string, description?: string, etaDate?: string) => Promise<void>;
  onUpdateDraftOrder?: (orderId: string, supplierName: string, supplierId: string | null, orderQuantities: Record<string, number>, title?: string, description?: string, etaDate?: string, unitCosts?: Record<string, number>) => Promise<void>;
  onSubmitDraftOrder?: (orderId: string, supplierName: string, supplierId: string | null) => Promise<void>;
  editingDraft?: PurchaseOrder | null; // Draft order being edited
  onCancelEdit?: () => void; // Function to cancel editing and return to view
}

const defaultFormValues: PurchaseOrderFormData = {
  supplier_name: "",
  title: "",
  description: "",
  eta_date: "",
};

// Define the custom size order - Adult sizes first, then kids sizes
const sizeOrder = ["XS", "S", "M", "L", "XL", "2X", "3X", "4X", "4", "6", "8", "10", "12", "14", "16"];
const SUPPLIER_EMPTY_VALUE = "__none__";
const SUPPLIER_ADD_NEW_VALUE = "__add_new_supplier__";

// Helper function to sort sizes in the custom order
const sortSizes = (sizes: string[]): string[] => {
  return [...sizes].sort((a, b) => {
    const indexA = sizeOrder.indexOf(a);
    const indexB = sizeOrder.indexOf(b);
    
    // If both sizes are in the custom order list
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    
    // If only a is in the custom order list
    if (indexA !== -1) {
      return -1;
    }
    
    // If only b is in the custom order list
    if (indexB !== -1) {
      return 1;
    }
    
    // If neither are in the custom order list, sort alphabetically
    return a.localeCompare(b);
  });
};

// BLU-571: shared inline unit-cost input — a $/£/€-prefixed decimal-masked
// field with the amber "pending edit" affordance (DESIGN.md §1). Used by both
// the list-view cost cell and the matrix cells so the two views present cost
// identically. `centered` aligns the wrapper for the centered matrix cells.
const InlineCostInput: React.FC<{
  productId: string;
  sku?: string;
  value: string;
  onChange: (productId: string, value: string) => void;
  pending: boolean;
  currencySymbol: string;
  centered?: boolean;
  // Ghosted hint showing the cost that will be used if the field is left blank
  // (catalog default for a new line; persisted cost for an existing one).
  placeholder?: string;
}> = ({ productId, sku, value, onChange, pending, currencySymbol, centered, placeholder }) => (
  <div className={cn("relative w-24", centered ? "mx-auto" : "ml-auto")}>
    <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
      {currencySymbol}
    </span>
    <Input
      type="text"
      inputMode="decimal"
      className={cn(
        "h-8 pl-5 text-right text-sm tabular-nums",
        pending && "border-amber-300 ring-1 ring-amber-300",
      )}
      value={value}
      onChange={(e) => onChange(productId, e.target.value)}
      placeholder={placeholder}
      aria-label={`Unit cost for ${sku ?? productId}`}
    />
  </div>
);

const SubmitOrderTab: React.FC<SubmitOrderTabProps> = ({
  products,
  loading,
  onCreateOrder,
  onCreateDraftOrder,
  onUpdateDraftOrder,
  onSubmitDraftOrder,
  editingDraft,
  onCancelEdit,
}) => {
  const isTransfer = editingDraft?.order_type === 'transfer';

  // React Hook Form
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty },
  } = useForm<PurchaseOrderFormData>({
    resolver: zodResolver(purchaseOrderFormSchema),
    defaultValues: defaultFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const supplierName = watch("supplier_name") ?? "";
  const orderTitle = watch("title") ?? "";
  const trimmedSupplierName = supplierName.trim();
  const trimmedOrderTitle = orderTitle.trim();
  const hasSupplierName = trimmedSupplierName.length > 0;
  const hasOrderTitle = trimmedOrderTitle.length > 0;
  const isEditingNonDraft = Boolean(editingDraft && editingDraft.status !== "draft");
  const { suppliers: supplierRecords, isLoading: suppliersLoading } = useSuppliers({ includeInactive: true });
  const activeSuppliers = useMemo(
    () => supplierRecords.filter((supplier) => supplier.is_active),
    [supplierRecords]
  );
  const supplierOptions = useMemo(() => {
    if (!trimmedSupplierName) {
      return activeSuppliers;
    }

    const existingSupplier = supplierRecords.find((supplier) => supplier.title === trimmedSupplierName);
    if (!existingSupplier || existingSupplier.is_active) {
      return activeSuppliers;
    }

    return activeSuppliers.some((supplier) => supplier.id === existingSupplier.id)
      ? activeSuppliers
      : [...activeSuppliers, existingSupplier];
  }, [activeSuppliers, supplierRecords, trimmedSupplierName]);
  const selectedSupplier = useMemo(
    () => supplierRecords.find((supplier) => supplier.title === trimmedSupplierName) ?? null,
    [supplierRecords, trimmedSupplierName]
  );
  // SKUs imported via CSV that aren't yet stocked at this location.
  // The backend auto-activates these on order save; until then we need
  // to render them in the form, so we merge them into the products list.
  const [csvExtraProducts, setCsvExtraProducts] = useState<Product[]>([]);
  const productsWithCsvExtras = useMemo(() => {
    if (csvExtraProducts.length === 0) return products;
    const existingIds = new Set(products.map((p) => p.id));
    const extras = csvExtraProducts.filter((p) => !existingIds.has(p.id));
    return extras.length === 0 ? products : [...products, ...extras];
  }, [products, csvExtraProducts]);
  // BLU-271: soft sort, not a filter — every SKU stays visible. Supplier's
  // exact-ID matches float to the top, backup matches next, everything else
  // keeps its original order (Array.prototype.sort is stable). Empty/no
  // supplier selected = unchanged order, matching prior no-supplier behaviour.
  const supplierSortedProducts = useMemo(() => {
    if (!selectedSupplier) {
      return productsWithCsvExtras;
    }
    return [...productsWithCsvExtras].sort(
      (a, b) => getSupplierMatchTier(a, selectedSupplier) - getSupplierMatchTier(b, selectedSupplier)
    );
  }, [productsWithCsvExtras, selectedSupplier]);
  const { format: formatCurrency, currency: orgCurrency } = useCurrencyFormatter();
  const { t } = useTranslation('inventory');
  const displayCurrency = useMemo(() => {
    const raw = editingDraft?.currency ?? selectedSupplier?.default_currency ?? orgCurrency;
    return String(raw ?? orgCurrency).trim().toUpperCase();
  }, [editingDraft, orgCurrency, selectedSupplier]);
  const formatMoney = useCallback(
    (value: number | null | undefined) => {
      if (value === null || value === undefined) return "-";
      return formatCurrency(value, displayCurrency);
    },
    [displayCurrency, formatCurrency]
  );
  const roundMoney = useCallback((value: number) => Math.round((value + Number.EPSILON) * 100) / 100, []);
  const currencySymbol = displayCurrency === "USD" ? "$" : displayCurrency === "GBP" ? "£" : displayCurrency === "EUR" ? "€" : "$";

  const receivedBySkuId = useMemo(() => {
    if (!editingDraft?.items || !isEditingNonDraft) return new Map<string, number>();
    return new Map(editingDraft.items.map(item => [item.sku_id, item.received_quantity ?? 0]));
  }, [editingDraft?.items, isEditingNonDraft]);

  // BLU-571: existing PO line items keyed by SKU id, so the inline unit-cost
  // cell can seed from — and save against — the item's own persisted cost
  // rather than the catalog default. Newly-added-this-session lines aren't
  // in here yet (no purchase_order_items row until the next save).
  const itemBySkuId = useMemo(() => {
    const map = new Map<string, PurchaseOrderItem>();
    editingDraft?.items?.forEach(item => {
      if (item.sku_id) map.set(item.sku_id, item);
    });
    return map;
  }, [editingDraft?.items]);

  const { canManagePurchaseOrders } = useRole();

  // BLU-571: mirrors OrderDetail.tsx's `canOverridePrice` — override is the
  // PRE-receive path, and gated on the same `purchase_orders.manage`
  // permission so an unauthorized user never sees an editable input that
  // would only fail server-side. Once any stock is received the WAC-adjust
  // dialog takes over (out of scope here); transfers have no per-item cost
  // override.
  const isCostEditable = Boolean(
    canManagePurchaseOrders() &&
    editingDraft &&
    !isTransfer &&
    editingDraft.status !== "cancelled" &&
    editingDraft.status !== "partially_received" &&
    editingDraft.status !== "received" &&
    editingDraft.payment_status !== "paid"
  );

  // Store order quantities keyed by product ID (managed separately from form)
  const [orderQuantities, setOrderQuantities] = useState<Record<string, number>>({});
  // BLU-571: pending unit-cost edits keyed by product ID, flushed to
  // `overrideItemPrice` (per line) alongside the qty save on "Save All".
  const [costDrafts, setCostDrafts] = useState<Record<string, string>>({});
  const [isCreating, setIsCreating] = useState(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [isUpdatingDraft, setIsUpdatingDraft] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<{
    type: string[];
    color: string[];
    size: string[];
  }>({
    type: [],
    color: [],
    size: []
  });
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const [addSkuSearch, setAddSkuSearch] = useState("");
  const [addSkuDropdownOpen, setAddSkuDropdownOpen] = useState(false);
  const addSkuDropdownRef = useRef<HTMLDivElement>(null);
  const [supplierChangeConfirmOpen, setSupplierChangeConfirmOpen] = useState(false);
  const [pendingSupplierName, setPendingSupplierName] = useState<string | null>(null);
  const pendingSupplierFieldRef = useRef<{ onChange: (value: string) => void } | null>(null);

  // Close add-SKU dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (addSkuDropdownRef.current && !addSkuDropdownRef.current.contains(e.target as Node)) {
        setAddSkuDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // BLU-571: guards the init effect below to run once per draft, not on every
  // `products` refetch (e.g. the invalidate a Save All fires while the user
  // stays on this page) — otherwise a background refetch would silently
  // clobber in-progress orderQuantities/costDrafts edits back to the
  // pre-session snapshot.
  const initializedDraftIdRef = useRef<string | null>(null);

  // Initialize form with draft data when editing
  useEffect(() => {
    if (!editingDraft || products.length === 0) {
      return;
    }
    if (initializedDraftIdRef.current === editingDraft.id) {
      return;
    }
    initializedDraftIdRef.current = editingDraft.id;

    reset({
      supplier_name: editingDraft.supplier_name || "",
      title: editingDraft.title || "",
      description: editingDraft.description || "",
      eta_date: editingDraft.eta_date || "",
    });

    // Convert draft items to orderQuantities format
    // We need to map from sku_id (in draft items) to product.id (for orderQuantities)
    const quantities: Record<string, number> = {};
    const costs: Record<string, string> = {};

    editingDraft.items?.forEach(item => {
      if (item.sku_id) {
        // Find the product that matches this sku_id
        const product = products.find(p => p.id === item.sku_id);
        if (product) {
          quantities[product.id] = item.ordered_quantity;
          const effectiveCost = item.overridden_unit_price ?? item.unit_cost;
          if (effectiveCost != null) {
            costs[product.id] = String(effectiveCost);
          }
        }
      }
    });

    setOrderQuantities(quantities);
    setCostDrafts(costs);
  }, [editingDraft, products, reset]);

  useEffect(() => {
    if (editingDraft) {
      return;
    }

    initializedDraftIdRef.current = null;
    reset(defaultFormValues);
    setOrderQuantities({});
    setCostDrafts({});
  }, [editingDraft, reset]);

  useEffect(() => {
    if (isTransfer || !trimmedSupplierName || suppliersLoading) {
      return;
    }

    const exists = supplierRecords.some((supplier) => supplier.title === trimmedSupplierName);
    if (!exists) {
      setValue("supplier_name", "", { shouldDirty: true, shouldValidate: true });
    }
  }, [isTransfer, setValue, supplierRecords, suppliersLoading, trimmedSupplierName]);

  // Color name to hex mapping for better UX
  const colorHexMap: Record<string, string> = {
    'black': '#000000',
    'natural': '#EBE9DF',
    'powder blue':'#0EA5E9',
    'charcoal': '#36454F',
    'lilac': '#9370DB',
    'pale blue': '#B0E0E6',
    'black ringer': '#000000',
    'red ringer': '#FF0000',
    'white': '#FFFFFF',
    'red': '#FF0000',
    'blue': '#0000FF',
    'green': '#008000',
    'yellow': '#FFFF00',
    'purple': '#800080',
    'pink': '#FFC0CB',
    'orange': '#FFA500',
    'brown': '#8B4513',
    'black stone': '#414040',
    'grey stone': '#535252',
    'grey': '#808080',
    'gray': '#808080',
    'navy': '#000080',
    'beige': '#F5F5DC',
    'cream': '#FFFDD0',
    'maroon': '#800000',
    'teal': '#008080',
    'olive': '#808000',
    'silver': '#C0C0C0',
    'gold': '#FFD700'
  };

  // Helper function to render color circle with special handling for ringers
  const renderColorCircle = (color: string) => {
    const isRinger = color.toLowerCase().includes('ringer');
    
    if (isRinger) {
      // Extract the ringer color (black/red)
      const ringerColor = color.toLowerCase().includes('black') ? '#000000' : '#FF0000';
      return (
        <div 
          className="w-4 h-4 rounded-full bg-white"
          style={{ 
            border: `2px solid ${ringerColor}`,
            boxSizing: 'border-box'
          }}
        />
      );
    } else {
      // Regular solid color circle
      return (
        <div 
          className="w-4 h-4 rounded-full border border-border"
          style={{ backgroundColor: colorHexMap[color.toLowerCase()] || '#CCCCCC' }}
        />
      );
    }
  };

  // Extract unique values for filters
  const uniqueTypes = useMemo(() => {
    return Array.from(new Set(supplierSortedProducts.filter(p => p.category.type).map(p => p.category.type))).sort() as string[];
  }, [supplierSortedProducts]);
  
  const uniqueColors = useMemo(() => {
    return Array.from(new Set(supplierSortedProducts.filter(p => p.category.color).map(p => p.category.color))).sort() as string[];
  }, [supplierSortedProducts]);
  
  const uniqueSizes = useMemo(() => {
    return sortSizes(
      Array.from(new Set(supplierSortedProducts.filter(p => p.category.size).map(p => p.category.size))) as string[]
    );
  }, [supplierSortedProducts]);

  // Set filter active state
  useEffect(() => {
    const hasActiveFilter = filters.type.length > 0 || filters.color.length > 0 || filters.size.length > 0;
    setIsFilterActive(hasActiveFilter);
  }, [filters]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      type: [],
      color: [],
      size: []
    });
  };

  // Smart search function
  const smartSearch = (product: Product, searchTerms: string[]): boolean => {
    if (searchTerms.length === 0) return true;
    
    const searchableFields = [
      product.sku,
      product.category.type || '',
      product.category.color || '',
      product.category.size || ''
    ].join(' ').toLowerCase();
    
    return searchTerms.every(term => 
      searchableFields.includes(term.toLowerCase())
    );
  };

  // Filter products
  const filteredProducts = useMemo(() => {
    return supplierSortedProducts.filter(product => {
      const searchTerms = searchTerm.trim().split(/\s+/).filter(term => term.length > 0);
      const matchesSearch = smartSearch(product, searchTerms);
      
      const matchesType = filters.type.length === 0 || (product.category.type && filters.type.includes(product.category.type));
      const matchesColor = filters.color.length === 0 || (product.category.color && filters.color.includes(product.category.color));
      const matchesSize = filters.size.length === 0 || (product.category.size && filters.size.includes(product.category.size));
      
      return matchesSearch && matchesType && matchesColor && matchesSize;
    });
  }, [supplierSortedProducts, searchTerm, filters]);

  // Products currently in the order — used in list view. Sorted by supplier
  // match tier first (BLU-271: exact match, then backup, then the rest),
  // SKU alphabetical within each tier. When editing, include items with
  // qty 0 (user may be clearing to retype).
  const orderProducts = useMemo(() => {
    return supplierSortedProducts
      .filter(p => editingDraft ? (p.id in orderQuantities) : (orderQuantities[p.id] ?? 0) > 0)
      .sort((a, b) => {
        const tierDiff = getSupplierMatchTier(a, selectedSupplier) - getSupplierMatchTier(b, selectedSupplier);
        return tierDiff !== 0 ? tierDiff : a.sku.localeCompare(b.sku);
      });
  }, [supplierSortedProducts, orderQuantities, editingDraft, selectedSupplier]);

  // Search results for the add-SKU dropdown in list view
  const MAX_ADD_RESULTS = 50;
  const addSkuResults = useMemo(() => {
    const terms = addSkuSearch.trim().split(/\s+/).filter(t => t.length > 0);
    if (terms.length === 0) return { available: [] as Product[], added: [] as Product[] };
    const lowerTerms = terms.map(t => t.toLowerCase());
    const available: Product[] = [];
    const added: Product[] = [];
    for (const product of supplierSortedProducts) {
      const haystack = [product.sku, product.category.type || "", product.category.color || "", product.category.size || ""].join(" ").toLowerCase();
      if (!lowerTerms.every(term => haystack.includes(term))) continue;
      if ((orderQuantities[product.id] ?? 0) > 0) {
        added.push(product);
      } else {
        available.push(product);
      }
      if (available.length >= MAX_ADD_RESULTS && added.length >= MAX_ADD_RESULTS) break;
    }
    return { available: available.slice(0, MAX_ADD_RESULTS), added: added.slice(0, MAX_ADD_RESULTS) };
  }, [supplierSortedProducts, addSkuSearch, orderQuantities]);

  const handleAddProduct = useCallback((productId: string) => {
    setOrderQuantities(prev => ({ ...prev, [productId]: Math.max(prev[productId] ?? 0, 1) }));
  }, []);

  const handleRemoveProduct = useCallback((productId: string) => {
    const minQty = isEditingNonDraft ? (receivedBySkuId.get(productId) ?? 0) : 0;
    if (minQty > 0) {
      setOrderQuantities(prev => ({ ...prev, [productId]: minQty }));
    } else {
      setOrderQuantities(prev => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      // BLU-571: drop any pending cost edit for the removed line too, otherwise
      // Save All would call overrideItemPrice on a just-deleted item and raise a
      // false "N line(s) failed" toast.
      setCostDrafts(prev => {
        if (!(productId in prev)) return prev;
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  }, [isEditingNonDraft, receivedBySkuId]);

  // Handle filter changes
  const handleFilterChange = useCallback((filterType: keyof typeof filters, value: string) => {
    setFilters(prevFilters => {
      const currentValues = prevFilters[filterType];
      const isChecked = currentValues.includes(value);
      
      if (isChecked) {
        return {
          ...prevFilters,
          [filterType]: currentValues.filter(v => v !== value)
        };
      } else {
        return {
          ...prevFilters,
          [filterType]: [...currentValues, value]
        };
      }
    });
  }, []);

  // BLU-577: the matrix view is a layout of the CURRENT order (mirrors
  // OrderCreate's matrixData, which iterates lineItemRows), not a catalog
  // browser — restrict to products already added to the order, still
  // narrowed by the table-mode search/filter toolbar.
  const matrixProducts = useMemo(() => {
    return filteredProducts.filter(p =>
      editingDraft ? (p.id in orderQuantities) : (orderQuantities[p.id] ?? 0) > 0
    );
  }, [filteredProducts, orderQuantities, editingDraft]);

  // Generate matrix view data grouped by type
  const matrixData = useMemo(() => {
    const groupedByType: Record<string, {
      colors: string[];
      sizes: string[];
      matrix: Record<string, Record<string, { productId: string; currentStock: number }>>;
    }> = {};

    matrixProducts.forEach(product => {
      const type = product.category.type || 'Unknown';
      const color = product.category.color || 'Unknown';
      const size = product.category.size || 'Unknown';

      if (!groupedByType[type]) {
        groupedByType[type] = {
          colors: [],
          sizes: [],
          matrix: {}
        };
      }

      if (!groupedByType[type].colors.includes(color)) {
        groupedByType[type].colors.push(color);
      }
      if (!groupedByType[type].sizes.includes(size)) {
        groupedByType[type].sizes.push(size);
      }

      if (!groupedByType[type].matrix[color]) {
        groupedByType[type].matrix[color] = {};
      }
      groupedByType[type].matrix[color][size] = {
        productId: product.id,
        currentStock: product.stockLevel
      };
    });

    // Sort colors and sizes for consistent display
    Object.keys(groupedByType).forEach(type => {
      const typeData = groupedByType[type];
      if (typeData) {
        typeData.colors.sort();
        typeData.sizes = sortSizes(typeData.sizes);
      }
    });

    return groupedByType;
  }, [matrixProducts]);

  const handleQuantityChange = (productId: string, quantity: string) => {
    const numQuantity = parseInt(quantity, 10);
    const minQty = isEditingNonDraft ? (receivedBySkuId.get(productId) ?? 0) : 0;

    if (quantity === "" || isNaN(numQuantity) || numQuantity < 0) {
      if (minQty > 0) {
        setOrderQuantities(prev => ({ ...prev, [productId]: minQty }));
      } else if (editingDraft) {
        // When editing, keep the item visible with 0 so the user can type a new value
        setOrderQuantities(prev => ({ ...prev, [productId]: 0 }));
      } else {
        const newQuantities = { ...orderQuantities };
        delete newQuantities[productId];
        setOrderQuantities(newQuantities);
      }
    } else {
      const clampedQty = Math.max(numQuantity, minQty);
      setOrderQuantities(prev => ({
        ...prev,
        [productId]: clampedQty
      }));
    }
  };

  // BLU-571: mirrors PriceOverrideCell's input mask — up to 2 decimal places,
  // no negative sign (overrideItemPrice requires a positive number).
  const handleCostInputChange = useCallback((productId: string, value: string) => {
    if (value !== "" && !/^\d*\.?\d{0,2}$/.test(value)) return;
    setCostDrafts(prev => ({ ...prev, [productId]: value }));
  }, []);

  // BLU-571: lines whose cost draft actually differs from the line's effective
  // baseline cost — only these are sent as per-line overrides on Save All, so
  // unrelated lines aren't re-written. Includes brand-new lines (no
  // purchase_order_items row yet), whose baseline is the catalog cost. Lines
  // with qty 0 (being cleared) are ignored.
  const pendingCostChanges = useMemo(() => {
    const changes: Array<{ skuId: string; value: number }> = [];
    Object.entries(costDrafts).forEach(([skuId, raw]) => {
      if ((orderQuantities[skuId] ?? 0) <= 0) return;
      const parsed = parseFloat(raw);
      if (isNaN(parsed) || parsed <= 0) return;
      const rounded = roundMoney(parsed);
      const existingItem = itemBySkuId.get(skuId);
      const baseline = existingItem
        ? existingItem.overridden_unit_price ?? existingItem.unit_cost ?? null
        : (() => {
            const product = productsWithCsvExtras.find((p) => p.id === skuId);
            return product?.weightedAverageCost ?? product?.effectiveCost ?? null;
          })();
      // baseline === null (no catalog cost) means any typed value is a change.
      if (baseline !== null && Math.abs(rounded - baseline) < 0.005) return;
      changes.push({ skuId, value: rounded });
    });
    return changes;
  }, [costDrafts, orderQuantities, itemBySkuId, productsWithCsvExtras, roundMoney]);

  // BLU-571: map of skuId -> overridden unit cost, sent to updateDraft alongside
  // orderQuantities so cost persists in the SAME "Save All" call as quantity
  // (no separate per-line mutation).
  const pendingUnitCosts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const { skuId, value } of pendingCostChanges) map[skuId] = value;
    return map;
  }, [pendingCostChanges]);

  // BLU-571: product ids (= sku_id) whose cost cell holds a pending, savable
  // edit — drives the amber "pending" affordance on the input (DESIGN.md §1
  // amber = editing/attention).
  const pendingCostSkuIds = useMemo(
    () => new Set(pendingCostChanges.map((c) => c.skuId)),
    [pendingCostChanges]
  );

  const handleApplyCsvItems = (items: Array<{ productId: string; quantity: number; newProduct?: Product }>) => {
    if (items.length === 0) return;
    setOrderQuantities((prev) => {
      const next = { ...prev };
      items.forEach((item) => {
        next[item.productId] = item.quantity;
      });
      return next;
    });
    // Merge any "new at location" products into local state so rows render
    // before the backend creates the sku_inventory row on order save.
    const newProducts = items
      .map((i) => i.newProduct)
      .filter((p): p is Product => Boolean(p));
    if (newProducts.length > 0) {
      setCsvExtraProducts((prev) => {
        const knownIds = new Set([
          ...products.map((p) => p.id),
          ...prev.map((p) => p.id),
        ]);
        const toAdd = newProducts.filter((p) => !knownIds.has(p.id));
        return toAdd.length === 0 ? prev : [...prev, ...toAdd];
      });
    }
  };

  const totalItems = useMemo(() => {
    return Object.values(orderQuantities).reduce((sum, qty) => sum + qty, 0);
  }, [orderQuantities]);

  const totalProducts = useMemo(() => {
    return Object.keys(orderQuantities).filter(key => (orderQuantities[key] ?? 0) > 0).length;
  }, [orderQuantities]);

  // BLU-571: effective unit cost for a line — pending inline edit first (so
  // totals react live to the tabbable cost cell), then the PO item's own
  // persisted cost (override or unit_cost), then the catalog default for a
  // line that isn't saved to this order yet.
  //
  // A draft value that is empty / NaN / <= 0 is NOT a persistable edit
  // (`pendingCostChanges` skips it; overrideItemPrice requires a positive
  // number), so we fall through to the persisted cost here rather than
  // previewing a $0.00 line total that would never actually be saved. This
  // keeps the live preview and what Save All writes in agreement.
  //
  // NOTE (display-vs-persist drift, pre-existing, out of scope for BLU-571):
  // this getter shows an existing line's persisted `unit_cost` /
  // `overridden_unit_price`, but `updateDraft` re-resolves `unit_cost` from
  // the current catalog on save for lines whose quantity changes. So a
  // drifted catalog cost can change an un-overridden line's cost on Save even
  // though the preview showed the persisted value. That is existing
  // `updateDraft` behaviour, not a bug in this cell — do not "fix" it here.
  const getEffectiveUnitCost = useCallback(
    (productId: string, product: Product | undefined): number | null => {
      const draftCost = costDrafts[productId];
      if (draftCost !== undefined && draftCost !== "") {
        const parsed = parseFloat(draftCost);
        if (!isNaN(parsed) && parsed > 0) return roundMoney(parsed);
      }
      const existingItem = itemBySkuId.get(productId);
      if (existingItem) {
        return existingItem.overridden_unit_price ?? existingItem.unit_cost ?? null;
      }
      return product?.weightedAverageCost ?? product?.effectiveCost ?? null;
    },
    [costDrafts, itemBySkuId, roundMoney]
  );

  const costRows = useMemo(() => {
    return Object.entries(orderQuantities)
      .filter(([, qty]) => qty > 0)
      .map(([productId, qty]) => {
        const product = productsWithCsvExtras.find(p => p.id === productId);
        const unitCost = getEffectiveUnitCost(productId, product);
        const lineTotal = unitCost === null ? null : roundMoney(unitCost * qty);
        return {
          product,
          quantity: qty,
          unitCost,
          lineTotal,
        };
      })
      .filter(
        (row): row is { product: Product; quantity: number; unitCost: number | null; lineTotal: number | null } =>
          Boolean(row.product)
      );
  }, [orderQuantities, productsWithCsvExtras, roundMoney, getEffectiveUnitCost]);

  const missingCostCount = useMemo(
    () => costRows.filter((row) => row.unitCost === null).length,
    [costRows]
  );

  const totalCost = useMemo(() => {
    if (missingCostCount > 0) {
      return null;
    }
    return roundMoney(costRows.reduce((sum, row) => sum + (row.lineTotal ?? 0), 0));
  }, [costRows, missingCostCount, roundMoney]);

  // BLU-571: snapshot of the draft's persisted quantities (keyed by product id,
  // which equals the item's sku_id) so "Save All" can be dirty-gated — a save
  // with nothing changed just re-writes identical rows.
  const editingDraftQuantities = useMemo(() => {
    const map = new Map<string, number>();
    editingDraft?.items?.forEach(item => {
      if (item.sku_id) map.set(item.sku_id, item.ordered_quantity);
    });
    return map;
  }, [editingDraft?.items]);

  // BLU-571: true when the edit flow has anything worth saving — a quantity
  // differs from the persisted snapshot, a line was added/removed, or a cost
  // draft is pending. The create flow has no snapshot to diff against, so it
  // keeps its existing behaviour (not dirty-gated here).
  const hasPendingChanges = useMemo(() => {
    if (!editingDraft) return true;
    if (pendingCostChanges.length > 0) return true;
    for (const key of Object.keys(orderQuantities)) {
      if ((orderQuantities[key] ?? 0) !== (editingDraftQuantities.get(key) ?? 0)) {
        return true;
      }
    }
    for (const [key, orig] of editingDraftQuantities) {
      if (!(key in orderQuantities) && orig !== 0) {
        return true;
      }
    }
    return false;
  }, [editingDraft, pendingCostChanges, orderQuantities, editingDraftQuantities]);

  const requiredFieldsSatisfied = isTransfer ? hasOrderTitle : (hasSupplierName && hasOrderTitle);

  const isSubmitOrderDisabled =
    isCreating ||
    isCreatingDraft ||
    totalItems === 0 ||
    !requiredFieldsSatisfied;

  const isSubmitDraftDisabled =
    isCreating ||
    isUpdatingDraft ||
    totalItems === 0 ||
    !requiredFieldsSatisfied;

  const isSaveDraftDisabled =
    isCreating ||
    isCreatingDraft ||
    totalItems === 0 ||
    !requiredFieldsSatisfied;

  const isSaveChangesDisabled =
    isCreating ||
    isUpdatingDraft ||
    totalItems === 0 ||
    !requiredFieldsSatisfied ||
    // BLU-571: "nothing to save" = no qty/cost change AND no header-field edit.
    // `hasPendingChanges` only tracks qty + cost (incl. per-line overrides);
    // without the `isDirty` term a header-only edit (title / supplier /
    // description / ETA) would leave Save greyed out and be silently
    // unsaveable — the react-hook-form dirty flag (seeded from the draft via
    // reset()) covers those fields.
    (!hasPendingChanges && !isDirty);

  const handleSubmitOrder = async (data: PurchaseOrderFormData) => {
    if (!validateOrderQuantities(orderQuantities)) {
      toast({
        title: "Cannot submit order",
        description: "Please add at least one item to your order",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);
    try {
      await onCreateOrder(
        data.supplier_name,
        selectedSupplier?.id ?? null,
        orderQuantities,
        data.title,
        data.description,
        data.eta_date || undefined
      );
      // Reset form after successful order creation
      reset(defaultFormValues);
      setOrderQuantities({});
      // Success notification is handled by the mutation
    } catch (error) {
      reportError('Error submitting order', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveAsDraft = async () => {
    if (totalItems === 0) {
      toast({
        title: "Cannot save draft",
        description: "Please add at least one item to your draft order",
        variant: "destructive"
      });
      return;
    }

    const { supplier_name, title, description, eta_date } = getValues();

    setIsCreatingDraft(true);
    try {
      await onCreateDraftOrder(supplier_name || "", selectedSupplier?.id ?? null, orderQuantities, title, description, eta_date || undefined);
      // Reset form after successful draft creation
      reset(defaultFormValues);
      setOrderQuantities({});
      // Success notification is handled by the mutation
    } catch (error) {
      reportError('Error saving draft order', error);
    } finally {
      setIsCreatingDraft(false);
    }
  };

  const handleUpdateDraft = async () => {
    if (!editingDraft || !onUpdateDraftOrder) {
      reportError('Cannot update draft: missing draft or update function');
      return;
    }

    if (totalItems === 0) {
      toast({
        title: "Cannot update draft",
        description: "Please add at least one item to your draft order",
        variant: "destructive"
      });
      return;
    }

    const { supplier_name, title, description, eta_date } = getValues();

    setIsUpdatingDraft(true);
    try {
      // BLU-571: quantities AND pending per-line cost overrides persist in the
      // one updateDraft call (unitCosts), so there is no separate cost step.
      await onUpdateDraftOrder(editingDraft.id, supplier_name || "", selectedSupplier?.id ?? null, orderQuantities, title, description, eta_date || undefined, pendingUnitCosts);
      // Success notification is handled by the mutation

      // Navigate back to view orders after successful update
      if (onCancelEdit) {
        onCancelEdit();
      }
    } catch (error) {
      reportError('Error updating draft order', error);
    } finally {
      setIsUpdatingDraft(false);
    }
  };

  const handleSubmitDraftOrder = async () => {
    if (!editingDraft) {
      reportError('Cannot submit: no draft being edited');
      return;
    }

    const { supplier_name, title, description, eta_date } = getValues();
    const trimmedSupplier = supplier_name.trim();
    const trimmedTitle = title?.trim() ?? "";

    if (!isTransfer && !trimmedSupplier) {
      toast({
        title: "Cannot submit draft",
        description: "Please provide a supplier name before submitting",
        variant: "destructive"
      });
      return;
    }

    if (!trimmedTitle) {
      toast({
        title: "Cannot submit draft",
        description: "Please provide an order title before submitting",
        variant: "destructive"
      });
      return;
    }

    if (totalItems === 0) {
      toast({
        title: "Cannot submit draft",
        description: "Please add at least one item to submit your order",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);
    try {
      // First update the draft with current changes — quantities and pending
      // per-line cost overrides ride the same call (unitCosts), so an edit made
      // just before hitting Submit isn't silently dropped.
      if (onUpdateDraftOrder) {
        await onUpdateDraftOrder(editingDraft.id, supplier_name || "", selectedSupplier?.id ?? null, orderQuantities, title, description, eta_date || undefined, pendingUnitCosts);
      }

      // Then submit the draft
      if (onSubmitDraftOrder) {
        await onSubmitDraftOrder(editingDraft.id, supplier_name, selectedSupplier?.id ?? null);
      }

      // Success notification is handled by the mutation

      // Navigate back to view orders
      if (onCancelEdit) {
        onCancelEdit();
      }
    } catch (error) {
      reportError('Error submitting draft order', error);
    } finally {
      setIsCreating(false);
    }
  };

  const downloadOrderCSV = () => {
    if (totalItems === 0) {
      // No items to download - silently return
      return;
    }

    const headers = ['SKU', 'Type', 'Color', 'Size', 'Current_Stock', 'Order_Quantity'];
    const rows = Object.entries(orderQuantities)
      .filter(([, qty]) => qty > 0)
      .map(([productId, qty]) => {
        const product = productsWithCsvExtras.find(p => p.id === productId);
        if (!product) {
          return null;
        }
        return [
          product.sku,
          product.category.type || '',
          product.category.color || '',
          product.category.size || '',
          product.stockLevel.toString(),
          qty.toString(),
        ];
      })
      .filter((row): row is string[] => row !== null);

    const csvContent = toCsvContent([headers, ...rows]);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `purchase-order-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    // Download happens automatically - no notification needed
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sticky TOP action bar — must be the FIRST child so position:sticky has
          content beneath it to stick over. Mirrors PoActionBar on the view
          screen: currency pill leftmost (only when PO currency differs from
          org base), thin divider, read-only status badge, totals; Save /
          Cancel cluster on the right. */}
      {totalItems > 0 && (
        <div
          className="sticky z-30 border-b bg-background/85 supports-[backdrop-filter]:bg-background/65 backdrop-blur -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8"
          style={{ top: "var(--top-header-height, 3.5rem)" }}
        >
          <div className="mx-auto flex max-w-screen-2xl flex-wrap items-end justify-between gap-x-4 gap-y-2 pt-3 pb-2.5">
            <div className="flex flex-wrap items-end gap-2">
              {/* Currency toggle intentionally omitted on the edit screen — the
                  form inputs are denominated in PO currency, so a display
                  toggle to org base would imply an editable conversion that
                  doesn't exist. The PO currency is implicit in the supplier. */}
              {editingDraft && (
                <Badge
                  variant="secondary"
                  className={cn(
                    "h-8 px-3 text-[11px] font-medium border-0",
                    STATUS_SELECT_CLASS[editingDraft.status],
                  )}
                >
                  {STATUS_LABELS[editingDraft.status]}
                </Badge>
              )}
              <Badge variant="secondary" className="h-8 px-3 text-[11px]">{totalProducts} Products</Badge>
              <Badge variant="secondary" className="h-8 px-3 text-[11px]">{totalItems} Items</Badge>
              <Badge variant="secondary" className="h-8 px-3 text-[11px] tabular-nums">{formatMoney(totalCost)}</Badge>
            </div>
            <div className="flex flex-wrap items-end gap-1.5">
              {/* CSV utilities live on the left of the action cluster — same
                  size/styling as the rest, separated from the primary
                  actions by a thin divider so they read as auxiliary. */}
              <Button
                variant="outline"
                type="button"
                size="sm"
                onClick={() => setIsCsvModalOpen(true)}
                disabled={isCreating || isCreatingDraft || isUpdatingDraft}
                className="h-8 px-3 text-xs gap-1.5"
              >
                <Upload className="h-3.5 w-3.5" />
                Upload CSV
              </Button>
              <Button
                variant="outline"
                type="button"
                size="sm"
                onClick={downloadOrderCSV}
                disabled={totalItems === 0}
                className="h-8 px-3 text-xs gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                Download CSV
              </Button>
              <span aria-hidden className="mx-1 h-5 w-px self-end bg-border/60" />

              {editingDraft ? (
                <>
                  {!isEditingNonDraft && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSubmit(async () => {
                        await handleSubmitDraftOrder();
                      })}
                      disabled={isSubmitDraftDisabled}
                      className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                    >
                      <Send className="h-3.5 w-3.5" />
                      {isCreating ? "Submitting…" : isTransfer ? "Submit Transfer" : "Submit Order"}
                    </Button>
                  )}
                  <Button
                    variant={isEditingNonDraft ? "default" : "outline"}
                    type="button"
                    size="sm"
                    onClick={handleUpdateDraft}
                    disabled={isSaveChangesDisabled}
                    className={cn(
                      "h-8 px-3 text-xs gap-1.5",
                      isEditingNonDraft && "bg-threadheads-purple text-white hover:bg-threadheads-purple/90",
                    )}
                  >
                    <Save className="h-3.5 w-3.5" />
                    {/* BLU-571: "Save All" persists both the pending quantity
                        edits (always) and any pending inline cost overrides
                        (handleUpdateDraft) in one action. */}
                    {isUpdatingDraft ? "Saving…" : "Save All"}
                  </Button>
                  <Button
                    variant="outline"
                    type="button"
                    size="sm"
                    onClick={onCancelEdit}
                    disabled={isCreating || isUpdatingDraft}
                    className="h-8 px-3 text-xs gap-1.5 border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                  >
                    <X className="h-3.5 w-3.5" />
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSubmit(handleSubmitOrder)}
                    disabled={isSubmitOrderDisabled}
                    className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {isCreating ? "Submitting…" : "Submit Order"}
                  </Button>
                  <Button
                    variant="outline"
                    type="button"
                    size="sm"
                    onClick={handleSaveAsDraft}
                    disabled={isSaveDraftDisabled}
                    className="h-8 px-3 text-xs gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {isCreatingDraft ? "Saving…" : "Save as Draft"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header Card — order metadata + form fields + actions.
          When editing an existing PO the title/supplier/status all live in
          the sticky TopHeader + action bar above, so the duplicate header
          is hidden. Create flow keeps it (no PO number to display in the
          chrome yet). */}
      <Card>
        {!editingDraft && (
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="text-2xl">New Order</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {isTransfer ? "Transfer Order" : (trimmedSupplierName || "No supplier selected")}
              </p>
            </div>
          </CardHeader>
        )}
        <CardContent className={cn("space-y-6", editingDraft && "pt-6")}>
          {isEditingNonDraft && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <strong>Warning:</strong> This order has already been{' '}
              {editingDraft?.status === 'partially_received'
                ? 'partially received'
                : editingDraft?.status?.replace('_', ' ')}.{' '}
              Changes to quantities or items will update the order but will not affect
              items that have already been received.
            </div>
          )}

          {/* Order Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">
                Order Title
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                id="title"
                placeholder="Enter order title"
                {...register("title")}
              />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title.message}</p>
              )}
            </div>
            {!isTransfer && (
              <div className="space-y-2">
                <Label htmlFor="supplier">
                  Supplier
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Controller
                  control={control}
                  name="supplier_name"
                  render={({ field }) => (
                    <Select
                      value={selectedSupplier?.id ?? SUPPLIER_EMPTY_VALUE}
                      onValueChange={(value) => {
                        if (value === SUPPLIER_ADD_NEW_VALUE) {
                          setIsSupplierModalOpen(true);
                          return;
                        }

                        const nextSupplierName =
                          value === SUPPLIER_EMPTY_VALUE
                            ? ""
                            : supplierOptions.find((supplier) => supplier.id === value)?.title ?? "";

                        if (nextSupplierName !== field.value) {
                          const hasItems = Object.values(orderQuantities).some((qty) => qty > 0);
                          if (hasItems) {
                            setPendingSupplierName(nextSupplierName);
                            pendingSupplierFieldRef.current = field;
                            setSupplierChangeConfirmOpen(true);
                            return;
                          }
                          setSearchTerm("");
                          setFilters({
                            type: [],
                            color: [],
                            size: []
                          });
                        }
                        field.onChange(nextSupplierName);
                      }}
                      disabled={suppliersLoading}
                    >
                      <SelectTrigger id="supplier">
                        <SelectValue placeholder={suppliersLoading ? "Loading suppliers..." : "Select a supplier"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SUPPLIER_ADD_NEW_VALUE}>+ Add new supplier</SelectItem>
                        <SelectItem value={SUPPLIER_EMPTY_VALUE}>Select supplier…</SelectItem>
                        {supplierOptions.length === 0 ? (
                          <SelectItem value="__no_suppliers__" disabled>
                            No suppliers available
                          </SelectItem>
                        ) : (
                          supplierOptions.map((supplier) => (
                            <SelectItem key={supplier.id} value={supplier.id}>
                              {supplier.title}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.supplier_name && (
                  <p className="text-sm text-destructive">{errors.supplier_name.message}</p>
                )}
              </div>
            )}
            <div className={isTransfer ? "space-y-2" : "space-y-2 md:col-span-2"}>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder={isTransfer ? "Enter transfer description" : "Enter order description"}
                {...register("description")}
              />
            </div>
            {!isTransfer && (
              <div className="space-y-2">
                {/* PR #1989 review: eta_date is the Ready ETA (the detail page also tracks an Arrival ETA). */}
                <Label htmlFor="eta_date">Ready ETA</Label>
                <Input
                  id="eta_date"
                  type="date"
                  {...register("eta_date")}
                />
              </div>
            )}
          </div>

          {/* Order Summary Badges */}
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="secondary">
              {totalProducts} Products
            </Badge>
            <Badge variant="secondary">
              {totalItems} Total Items
            </Badge>
            <Badge variant="secondary">
              Total Cost {formatMoney(totalCost)}
            </Badge>
            {missingCostCount > 0 && (
              <span className="text-xs text-muted-foreground">
                Missing cost for {missingCostCount} SKU{missingCostCount === 1 ? "" : "s"}.
              </span>
            )}
          </div>

          {/* Action Buttons — only rendered for the create flow. While
              editing, all actions live in the sticky top bar (no duplicates).
              CSV upload/download has also moved into the sticky bar so it's
              accessible without scrolling back to the form. */}
          {!editingDraft && (
            <div className="flex flex-wrap items-end gap-1.5">
              <Button
                type="button"
                size="sm"
                onClick={handleSubmit(handleSubmitOrder)}
                disabled={isSubmitOrderDisabled}
                className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              >
                <Plus className="h-3.5 w-3.5" />
                {isCreating ? "Submitting…" : "Submit Order"}
              </Button>
              <Button
                variant="outline"
                type="button"
                size="sm"
                onClick={handleSaveAsDraft}
                disabled={isSaveDraftDisabled}
                className="h-8 px-3 text-xs gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                {isCreatingDraft ? "Saving…" : "Save as Draft"}
              </Button>
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            {isTransfer
              ? "Required: order title and at least one product line item."
              : "Required: order title, supplier name, and at least one product line item."}
          </p>
        </CardContent>
      </Card>

      {/* Tabbed Content */}
      <Tabs defaultValue="line-items">
        <TabsList>
          <TabsTrigger value="line-items">Line Items</TabsTrigger>
          <TabsTrigger value="cost-summary">
            Cost Summary{costRows.length > 0 && ` (${costRows.length})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="line-items" className="space-y-4">
          {/* View Toggle + mode-specific toolbar */}
          <div className="flex items-center space-x-2">
            {viewMode === 'list' ? (
              /* List mode: search-to-add dropdown */
              <div className="relative flex-1" ref={addSkuDropdownRef}>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search SKUs to add... (e.g., 'tee black M')"
                  value={addSkuSearch}
                  onChange={(e) => {
                    setAddSkuSearch(e.target.value);
                    if (e.target.value.trim()) setAddSkuDropdownOpen(true);
                  }}
                  onFocus={() => {
                    if (addSkuSearch.trim()) setAddSkuDropdownOpen(true);
                  }}
                  className="pl-10"
                />
                {addSkuDropdownOpen && addSkuSearch.trim() && (
                  <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border bg-popover shadow-lg">
                    <div className="max-h-[320px] overflow-y-auto">
                      {addSkuResults.available.length === 0 && addSkuResults.added.length === 0 && (
                        <div className="py-6 text-center text-sm text-muted-foreground">No SKUs found.</div>
                      )}
                      {addSkuResults.available.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                          onClick={() => {
                            handleAddProduct(product.id);
                            setAddSkuSearch("");
                            setAddSkuDropdownOpen(false);
                          }}
                        >
                          <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="font-mono text-xs font-medium shrink-0">{product.sku}</span>
                          <span className="truncate text-muted-foreground">
                            {[product.category.type, product.category.color, product.category.size].filter(Boolean).join(" · ")}
                          </span>
                          {selectedSupplier && getSupplierMatchTier(product, selectedSupplier) === SUPPLIER_MATCH_TIER.none && (
                            <Badge variant="muted" className="shrink-0 text-[10px] font-normal text-muted-foreground">
                              other supplier
                            </Badge>
                          )}
                          {product.stockLevel === 0 ? (
                            <Badge variant="outline" className="ml-auto shrink-0 text-[10px] tabular-nums">
                              0 stock
                            </Badge>
                          ) : (
                            <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
                              {product.stockLevel} in stock
                            </span>
                          )}
                        </button>
                      ))}
                      {addSkuResults.added.length > 0 && (
                        <>
                          {addSkuResults.available.length > 0 && <div className="border-t" />}
                          {addSkuResults.added.map((product) => (
                            <div
                              key={product.id}
                              className="flex w-full items-center gap-3 px-3 py-2 text-sm opacity-50"
                            >
                              <span className="h-4 w-4 shrink-0" />
                              <span className="font-mono text-xs font-medium shrink-0">{product.sku}</span>
                              <span className="truncate text-muted-foreground">
                                {[product.category.type, product.category.color, product.category.size].filter(Boolean).join(" · ")}
                              </span>
                              <Badge variant="secondary" className="ml-auto text-xs">Added</Badge>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Table mode: product search + filter popover */
              <>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search products... (e.g., 'tee black M')"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={isFilterActive ? "default" : "outline"}
                      size="icon"
                      className={isFilterActive ? "bg-threadheads-purple text-white hover:bg-threadheads-purple/90 hover:text-white" : ""}
                      title="Filters"
                    >
                      {isFilterActive ? <FilterX className="h-4 w-4" /> : <Filter className="h-4 w-4" />}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[800px] p-4">
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm">Filter Products</h4>
                      <div className="grid grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
                            {uniqueTypes.length > 0 ? (
                              uniqueTypes.map(type => (
                                <div key={type} className="flex items-center space-x-2 py-1">
                                  <Checkbox
                                    id={`po-type-${type}`}
                                    checked={filters.type.includes(type)}
                                    onCheckedChange={() => handleFilterChange("type", type)}
                                  />
                                  <Label htmlFor={`po-type-${type}`} className="text-sm cursor-pointer">{type}</Label>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-muted-foreground">No types available</div>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Color</Label>
                          <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
                            {uniqueColors.length > 0 ? (
                              uniqueColors.map(color => (
                                <div key={color} className="flex items-center space-x-2 py-1">
                                  <Checkbox
                                    id={`po-color-${color}`}
                                    checked={filters.color.includes(color)}
                                    onCheckedChange={() => handleFilterChange("color", color)}
                                  />
                                  <Label htmlFor={`po-color-${color}`} className="text-sm cursor-pointer">{color}</Label>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-muted-foreground">No colors available</div>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Size</Label>
                          <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
                            {uniqueSizes.length > 0 ? (
                              uniqueSizes.map(size => (
                                <div key={size} className="flex items-center space-x-2 py-1">
                                  <Checkbox
                                    id={`po-size-${size}`}
                                    checked={filters.size.includes(size)}
                                    onCheckedChange={() => handleFilterChange("size", size)}
                                  />
                                  <Label htmlFor={`po-size-${size}`} className="text-sm cursor-pointer">{size}</Label>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-muted-foreground">No sizes available</div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between pt-2">
                        <Button variant="outline" size="sm" onClick={clearFilters}>Clear All</Button>
                        <div className="text-xs text-muted-foreground pt-2">
                          {filteredProducts.length} of {supplierSortedProducts.length} items
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </>
            )}
            <div className="flex items-center rounded-md border">
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-2.5 rounded-r-none"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-2.5 rounded-l-none"
                onClick={() => setViewMode('table')}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Active Filters Display — table mode only */}
          {viewMode === 'table' && isFilterActive && (
            <div className="flex items-center justify-between bg-muted/30 p-3 rounded-md">
              <div className="flex items-center space-x-2 text-sm flex-wrap gap-1">
                <span className="font-medium">Active filters:</span>
                {filters.type.length > 0 && <Badge variant="secondary">Type: {filters.type.join(", ")}</Badge>}
                {filters.color.length > 0 && <Badge variant="secondary">Color: {filters.color.join(", ")}</Badge>}
                {filters.size.length > 0 && <Badge variant="secondary">Size: {filters.size.join(", ")}</Badge>}
              </div>
              <Button variant="ghost" size="sm" onClick={clearFilters}>Clear All</Button>
            </div>
          )}

          {viewMode === 'list' ? (
            /* List View — only items in the order */
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[48px]"></TableHead>
                    <TableHead className="w-[240px]">SKU</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[100px] text-right">In Stock</TableHead>
                    <TableHead className="w-[120px] text-right">Qty</TableHead>
                    <TableHead className="w-[120px] text-right">Unit Cost</TableHead>
                    <TableHead className="w-[140px] text-right">Line Total</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orderProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                        No line items yet. Search for SKUs above to add them.
                      </TableCell>
                    </TableRow>
                  ) : (
                    orderProducts.map((product) => {
                      const qty = orderQuantities[product.id] ?? 0;
                      const existingItem = itemBySkuId.get(product.id);
                      const unitCost = getEffectiveUnitCost(product.id, product);
                      const lineTotal = unitCost !== null && qty > 0 ? roundMoney(unitCost * qty) : null;
                      const { status } = getStockStatus(product.stockLevel, product.notify || null);
                      const minQty = isEditingNonDraft ? (receivedBySkuId.get(product.id) ?? 0) : 0;
                      // BLU-571: cost is inline-editable for every line
                      // (including brand-new lines) — a new line's cost rides
                      // the same updateDraft call and is stored as the item's
                      // override on insert.
                      const canEditThisCost = isCostEditable;

                      return (
                        <TableRow key={product.id}>
                          <TableCell>
                            <ProductImageWithHover
                              imageUrl={product.image_url}
                              productSku={product.sku}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm font-medium">{product.sku}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {product.title ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-sm font-medium">{product.title}</span>
                                  {[product.category.type, product.category.color, product.category.size].filter(Boolean).length > 0 && (
                                    <span className="text-xs text-muted-foreground">
                                      {[product.category.type, product.category.color, product.category.size].filter(Boolean).join(" · ")}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="text-sm text-muted-foreground">
                                  {[product.category.type, product.category.color, product.category.size].filter(Boolean).join(" · ")}
                                </div>
                              )}
                              {selectedSupplier && getSupplierMatchTier(product, selectedSupplier) === SUPPLIER_MATCH_TIER.none && (
                                <Badge variant="muted" className="shrink-0 text-[10px] font-normal text-muted-foreground">
                                  other supplier
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <span
                              className="text-sm font-medium"
                              style={{
                                color: status === 'critical' ? '#EF4444' : status === 'low' ? '#EBB305' : 'inherit',
                              }}
                            >
                              {product.stockLevel}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min={minQty}
                              className="w-20 ml-auto h-8 text-sm text-right"
                              value={orderQuantities[product.id] || ""}
                              onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                              placeholder="0"
                            />
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {canEditThisCost ? (
                              <InlineCostInput
                                productId={product.id}
                                sku={product.sku}
                                value={costDrafts[product.id] ?? ""}
                                onChange={handleCostInputChange}
                                pending={pendingCostSkuIds.has(product.id)}
                                currencySymbol={currencySymbol}
                                placeholder={unitCost != null ? unitCost.toFixed(2) : undefined}
                              />
                            ) : (
                              <span className="inline-flex items-center justify-end gap-1.5">
                                {/* BLU-388: indicator on the LEFT of the value so
                                    the rightmost glyph stays a digit and tabular
                                    alignment isn't broken across rows. Only renders
                                    for a not-yet-saved line still showing the live
                                    catalog default — an existing item's own
                                    persisted cost has nothing to convert from. */}
                                {!existingItem
                                  && product.weightedAverageCost == null
                                  && product.categoryCostSourceAmount != null
                                  && product.categoryCostCurrency
                                  && product.categoryCostCurrency !== displayCurrency && (
                                  <ConvertedCostIndicator
                                    sourceAmount={product.categoryCostSourceAmount}
                                    sourceCurrency={product.categoryCostCurrency}
                                    displayCurrency={displayCurrency}
                                    rate={
                                      unitCost !== null && product.categoryCostSourceAmount > 0
                                        ? unitCost / product.categoryCostSourceAmount
                                        : null
                                    }
                                  />
                                )}
                                <span className="tabular-nums">
                                  {unitCost !== null ? formatMoney(unitCost) : "-"}
                                </span>
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            {lineTotal !== null ? formatMoney(lineTotal) : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleRemoveProduct(product.id)}
                              disabled={minQty > 0}
                              title={minQty > 0 ? "Cannot remove: items already received" : "Remove from order"}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            /* Matrix/Table View — grouped by type */
            <div className="space-y-8">
              {Object.entries(matrixData).map(([type, data]) => (
                <div key={type} className="bg-card rounded-lg border p-6">
                  <h3 className="text-lg font-semibold mb-4">{type}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-border p-2 bg-muted text-left font-medium">Color</th>
                          {data.sizes.map(size => (
                            <th key={size} className="border border-border p-2 bg-muted text-center font-medium min-w-20">
                              {size}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.colors.map(color => {
                          const sampleProduct = filteredProducts.find(p =>
                            p.category.type === type && p.category.color === color
                          );

                          return (
                            <tr key={color}>
                              <td className="border border-border p-2 bg-card">
                                <div className="flex items-center space-x-2">
                                  <ProductImageWithHover
                                    imageUrl={sampleProduct?.image_url}
                                    productSku={`${type} - ${color}`}
                                  />
                                  {renderColorCircle(color)}
                                  <span className="capitalize">{color}</span>
                                </div>
                              </td>
                            {data.sizes.map(size => {
                              const cellData = data.matrix[color]?.[size];
                              if (!cellData) {
                                return (
                                  <td key={`${color}-${size}`} className="border border-border p-2 text-center">
                                    <div className="text-muted-foreground text-sm">-</div>
                                  </td>
                                );
                              }

                              // Resolve the product once for the SKU label, cost
                              // input and stock-status colour.
                              const cellProduct = productsWithCsvExtras.find(p => p.id === cellData.productId);
                              const cellSkuLabel = cellProduct?.sku ?? `${type} ${color} ${size}`;
                              const cellUnitCost = getEffectiveUnitCost(cellData.productId, cellProduct);
                              const { status: cellStockStatus } = getStockStatus(cellData.currentStock, cellProduct?.notify || null);

                              return (
                                <td key={cellData.productId || `${color}-${size}`} className="border border-border p-2 text-center">
                                  <div className="space-y-1 flex flex-col items-center">
                                    <Input
                                      type="number"
                                      min={isEditingNonDraft ? (receivedBySkuId.get(cellData.productId) ?? 0) : 0}
                                      className="w-24 h-8 text-sm"
                                      style={{ textAlign: 'center' }}
                                      name={`quantity-${cellData.productId}`}
                                      value={orderQuantities[cellData.productId] || ""}
                                      onChange={(e) => handleQuantityChange(cellData.productId, e.target.value)}
                                      placeholder="0"
                                      aria-label={`Order quantity for ${cellSkuLabel}`}
                                    />
                                    {/* BLU-571: unit cost is editable in the matrix view too, mirroring
                                        the list view (same InlineCostInput). Rendered only when cost
                                        editing is allowed (edit flow, unpaid, non-received, non-transfer);
                                        tab order is qty then cost per cell. */}
                                    {isCostEditable && (
                                      <InlineCostInput
                                        productId={cellData.productId}
                                        sku={cellSkuLabel}
                                        value={costDrafts[cellData.productId] ?? ""}
                                        onChange={handleCostInputChange}
                                        pending={pendingCostSkuIds.has(cellData.productId)}
                                        currencySymbol={currencySymbol}
                                        centered
                                        placeholder={cellUnitCost != null ? cellUnitCost.toFixed(2) : undefined}
                                      />
                                    )}
                                    <div
                                      className="text-xs font-medium"
                                      style={{
                                        color:
                                          cellStockStatus === 'critical'
                                            ? '#EF4444'
                                            : cellStockStatus === 'low'
                                              ? '#EBB305'
                                              : 'inherit',
                                      }}
                                    >
                                      Stock: {cellData.currentStock}
                                    </div>
                                    {isEditingNonDraft && (receivedBySkuId.get(cellData.productId) ?? 0) > 0 && (
                                      <div className="text-xs text-muted-foreground">
                                        Rcvd: {receivedBySkuId.get(cellData.productId)}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cost-summary">
          {costRows.length > 0 ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <CardTitle className="text-lg">Cost Breakdown</CardTitle>
                <Badge variant="secondary">
                  Total Cost {formatMoney(totalCost)}
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Cost</TableHead>
                        <TableHead className="text-right">Line Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {costRows.map((row) => (
                        <TableRow key={row.product.id}>
                          <TableCell className="font-mono text-sm">{row.product.sku}</TableCell>
                          <TableCell className="text-right">{row.quantity}</TableCell>
                          <TableCell className="text-right">
                            {formatMoney(row.unitCost)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatMoney(row.lineTotal)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {missingCostCount > 0 && (
                  <p className="text-xs text-muted-foreground mt-3">
                    {t('submitOrder.missingCostHint')}
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No items added yet. Add quantities on the Line Items tab to see cost breakdown.
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <PurchaseOrderCsvUploadModal
        products={products}
        isOpen={isCsvModalOpen}
        onClose={() => setIsCsvModalOpen(false)}
        onApply={handleApplyCsvItems}
      />
      <QuickAddSupplierModal
        open={isSupplierModalOpen}
        onOpenChange={setIsSupplierModalOpen}
      />

      {/* Supplier change confirmation dialog */}
      <Dialog open={supplierChangeConfirmOpen} onOpenChange={setSupplierChangeConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Change Supplier?
            </DialogTitle>
            <DialogDescription>
              Changing the supplier will clear all {totalProducts} item{totalProducts !== 1 ? "s" : ""} and
              their quantities from this order. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPendingSupplierName(null);
                pendingSupplierFieldRef.current = null;
                setSupplierChangeConfirmOpen(false);
              }}
            >
              Keep Current Supplier
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (pendingSupplierName !== null && pendingSupplierFieldRef.current) {
                  setOrderQuantities({});
                  setSearchTerm("");
                  setFilters({ type: [], color: [], size: [] });
                  pendingSupplierFieldRef.current.onChange(pendingSupplierName);
                }
                setPendingSupplierName(null);
                pendingSupplierFieldRef.current = null;
                setSupplierChangeConfirmOpen(false);
              }}
            >
              Change Supplier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubmitOrderTab; 
